import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, ChevronLeft, ChevronRight, Delete, Edit, Refresh } from '@mui/icons-material';
import {
  createPurchasingAccount,
  createPurchasingPlatform,
  deletePurchasingAccount,
  deletePurchasingPlatform,
  getPurchasingManagementOptions,
  getPurchasingAccounts,
  getPurchasingPlatforms,
  updatePurchasingAccount,
  updatePurchasingPlatform,
} from '../../services/order';

const normalizeRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const CustomPagination = ({
  page,
  rowsPerPage,
  totalRows,
  setPage,
  setRowsPerPage,
  customRowsInput,
  setCustomRowsInput,
  customRowsActive,
  setCustomRowsActive,
}) => {
  const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;
  const handlePrevPage = () => page > 0 && setPage(page - 1);
  const handleNextPage = () => page < totalPages - 1 && setPage(page + 1);
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
      return;
    }
    setRowsPerPage(Number(value));
    setPage(0);
    setCustomRowsActive(false);
  };
  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-end' }, mt: 1, gap: 2, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={handlePrevPage} disabled={page === 0}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="body2">Page {totalPages === 0 ? 0 : page + 1} of {totalPages}</Typography>
        <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
          <ChevronRight />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2">Rows per page</Typography>
        {!customRowsActive ? (
          <TextField
            select
            size="small"
            value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
            onChange={handleRowsPerPageChange}
            sx={{ width: 100 }}
          >
            {[10, 20, 50, 100].map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
            <MenuItem value="custom">Custom</MenuItem>
          </TextField>
        ) : (
          <TextField
            size="small"
            value={customRowsInput}
            onChange={(e) => setCustomRowsInput(e.target.value.replace(/\D/g, ''))}
            onBlur={handleCustomRowsInputBlur}
            autoFocus
            sx={{ width: 100 }}
            inputProps={{ min: 1, style: { textAlign: 'center' } }}
            placeholder="Rows"
          />
        )}
      </Box>
    </Box>
  );
};

const PurchasingReferences = () => {
  const user = useSelector((state) => state.auth.user);
  const [platformRows, setPlatformRows] = useState([]);
  const [accountRows, setAccountRows] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [openPlatformForm, setOpenPlatformForm] = useState(false);
  const [openAccountForm, setOpenAccountForm] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: '', row: null });

  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [platformForm, setPlatformForm] = useState({ name: '', is_active: true });
  const [accountForm, setAccountForm] = useState({ platform: '', purchasing_account: '', owner: '', is_active: true });

  const [platformPage, setPlatformPage] = useState(0);
  const [platformRowsPerPage, setPlatformRowsPerPage] = useState(10);
  const [platformCustomRowsInput, setPlatformCustomRowsInput] = useState('');
  const [platformCustomRowsActive, setPlatformCustomRowsActive] = useState(false);

  const [accountPage, setAccountPage] = useState(0);
  const [accountRowsPerPage, setAccountRowsPerPage] = useState(10);
  const [accountCustomRowsInput, setAccountCustomRowsInput] = useState('');
  const [accountCustomRowsActive, setAccountCustomRowsActive] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const includeOwners = !!user?.is_superuser;
      const tasks = [
        getPurchasingPlatforms(),
        getPurchasingAccounts(),
        includeOwners ? getPurchasingManagementOptions(true) : Promise.resolve(null),
      ];

      const [platformsResult, accountsResult, optionsResult] = await Promise.allSettled(tasks);

      if (platformsResult.status === 'fulfilled') {
        setPlatformRows(normalizeRows(platformsResult.value));
      } else {
        setPlatformRows([]);
        setSnackbar({
          open: true,
          severity: 'error',
          message: platformsResult.reason?.message || 'Failed to fetch purchasing platforms',
        });
      }

      if (accountsResult.status === 'fulfilled') {
        setAccountRows(normalizeRows(accountsResult.value));
      } else {
        setAccountRows([]);
        setSnackbar({
          open: true,
          severity: 'error',
          message: accountsResult.reason?.message || 'Failed to fetch purchasing accounts',
        });
      }

      if (includeOwners) {
        if (optionsResult.status === 'fulfilled') {
          setOwners(optionsResult.value?.owners || []);
        } else {
          setOwners([]);
          setSnackbar({
            open: true,
            severity: 'error',
            message: optionsResult.reason?.message || 'Failed to fetch purchasing owners',
          });
        }
      } else {
        setOwners(
          user
            ? [{ id: user.id, username: user.username, email: user.email }]
            : []
        );
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user?.id]);

  useEffect(() => {
    const totalPages = Math.ceil(platformRows.length / platformRowsPerPage) || 1;
    if (platformPage > totalPages - 1) setPlatformPage(Math.max(totalPages - 1, 0));
  }, [platformRows.length, platformRowsPerPage, platformPage]);

  useEffect(() => {
    const totalPages = Math.ceil(accountRows.length / accountRowsPerPage) || 1;
    if (accountPage > totalPages - 1) setAccountPage(Math.max(totalPages - 1, 0));
  }, [accountRows.length, accountRowsPerPage, accountPage]);

  const paginatedPlatforms = useMemo(() => {
    const start = platformPage * platformRowsPerPage;
    return platformRows.slice(start, start + platformRowsPerPage);
  }, [platformRows, platformPage, platformRowsPerPage]);

  const paginatedAccounts = useMemo(() => {
    const start = accountPage * accountRowsPerPage;
    return accountRows.slice(start, start + accountRowsPerPage);
  }, [accountRows, accountPage, accountRowsPerPage]);

  const platformColumns = useMemo(() => ([
    { field: 'name', headerName: 'Platform', flex: 1, minWidth: 220 },
    { field: 'is_active', headerName: 'Active', width: 120, valueGetter: (value, row) => (row?.is_active ? 'Yes' : 'No') },
    { field: 'updated_at', headerName: 'Updated At', minWidth: 180, valueGetter: (value, row) => (row?.updated_at ? new Date(row.updated_at).toLocaleString() : '-') },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <IconButton size="small" color="primary" onClick={() => { setSelectedPlatform(row); setPlatformForm({ name: row.name || '', is_active: !!row.is_active }); setOpenPlatformForm(true); }}>
            <Edit />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => { setDeleteTarget({ type: 'platform', row }); setOpenDelete(true); }}>
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ]), []);

  const accountColumns = useMemo(() => ([
    { field: 'platform_name', headerName: 'Platform', flex: 1, minWidth: 180 },
    { field: 'purchasing_account', headerName: 'Account', flex: 1, minWidth: 220 },
    { field: 'owner_name', headerName: 'Owner', minWidth: 180, valueGetter: (value, row) => row?.owner_name || '-' },
    { field: 'is_active', headerName: 'Active', width: 120, valueGetter: (value, row) => (row?.is_active ? 'Yes' : 'No') },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              setSelectedAccount(row);
              setAccountForm({
                platform: row.platform || '',
                purchasing_account: row.purchasing_account || '',
                owner: row.owner || '',
                is_active: !!row.is_active,
              });
              setOpenAccountForm(true);
            }}
          >
            <Edit />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => { setDeleteTarget({ type: 'account', row }); setOpenDelete(true); }}>
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ]), []);

  const handleSavePlatform = async () => {
    const name = (platformForm.name || '').trim();
    if (!name) {
      setSnackbar({ open: true, message: 'Platform name is required', severity: 'error' });
      return;
    }
    try {
      const payload = { name, is_active: platformForm.is_active };
      if (selectedPlatform) {
        await updatePurchasingPlatform(selectedPlatform.id, payload);
        setSnackbar({ open: true, message: 'Platform updated successfully', severity: 'success' });
      } else {
        await createPurchasingPlatform(payload);
        setSnackbar({ open: true, message: 'Platform created successfully', severity: 'success' });
      }
      setOpenPlatformForm(false);
      setSelectedPlatform(null);
      await loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleSaveAccount = async () => {
    const payload = {
      platform: accountForm.platform,
      purchasing_account: (accountForm.purchasing_account || '').trim(),
      owner: accountForm.owner || undefined,
      is_active: accountForm.is_active,
    };
    if (!payload.platform || !payload.purchasing_account) {
      setSnackbar({ open: true, message: 'Platform and account are required', severity: 'error' });
      return;
    }
    try {
      if (selectedAccount) {
        await updatePurchasingAccount(selectedAccount.id, payload);
        setSnackbar({ open: true, message: 'Account updated successfully', severity: 'success' });
      } else {
        await createPurchasingAccount(payload);
        setSnackbar({ open: true, message: 'Account created successfully', severity: 'success' });
      }
      setOpenAccountForm(false);
      setSelectedAccount(null);
      await loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.row?.id) return;
    try {
      if (deleteTarget.type === 'platform') {
        await deletePurchasingPlatform(deleteTarget.row.id);
        setSnackbar({ open: true, message: 'Platform deleted successfully', severity: 'success' });
      } else {
        await deletePurchasingAccount(deleteTarget.row.id);
        setSnackbar({ open: true, message: 'Account deleted successfully', severity: 'success' });
      }
      setOpenDelete(false);
      setDeleteTarget({ type: '', row: null });
      await loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography variant="h4" sx={{ mb: 1, color: '#DC143C', fontWeight: 'bold' }}>
            Purchasing Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Platforms are global. Accounts are owner-specific and tied to admin users.
          </Typography>
        </Grid>

        <Grid size={12}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={loadData} disabled={loading}>Refresh</Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => { setSelectedPlatform(null); setPlatformForm({ name: '', is_active: true }); setOpenPlatformForm(true); }}>Add Platform</Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setSelectedAccount(null);
                setAccountForm({
                  platform: '',
                  purchasing_account: '',
                  owner: user?.id || owners[0]?.id || '',
                  is_active: true,
                });
                setOpenAccountForm(true);
              }}
            >
              Add Account
            </Button>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 1.5, pb: 0 }}>
                <Typography variant="h6">Purchasing Platforms</Typography>
              </Box>
              <Box sx={{ width: '100%', overflowX: 'auto', minHeight: 430 }}>
                <DataGrid
                  rows={paginatedPlatforms}
                  columns={platformColumns}
                  loading={loading}
                  pageSize={platformRowsPerPage}
                  rowsPerPageOptions={[]}
                  hideFooter
                  disableSelectionOnClick
                  getRowId={(row) => row.id}
                  sx={{
                    border: 'none',
                    minWidth: 700,
                    '& .MuiDataGrid-columnHeader': { backgroundColor: (theme) => theme.palette.primary.main },
                    '& .MuiDataGrid-columnHeaderTitle': { color: 'white', fontWeight: 'bold' },
                    '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': { color: 'white' },
                    '& .MuiDataGrid-row:hover': { backgroundColor: '#FFF0F3' },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
          <CustomPagination
            page={platformPage}
            rowsPerPage={platformRowsPerPage}
            totalRows={platformRows.length}
            setPage={setPlatformPage}
            setRowsPerPage={setPlatformRowsPerPage}
            customRowsInput={platformCustomRowsInput}
            setCustomRowsInput={setPlatformCustomRowsInput}
            customRowsActive={platformCustomRowsActive}
            setCustomRowsActive={setPlatformCustomRowsActive}
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 1.5, pb: 0 }}>
                <Typography variant="h6">Purchasing Accounts</Typography>
              </Box>
              <Box sx={{ width: '100%', overflowX: 'auto', minHeight: 430 }}>
                <DataGrid
                  rows={paginatedAccounts}
                  columns={accountColumns}
                  loading={loading}
                  pageSize={accountRowsPerPage}
                  rowsPerPageOptions={[]}
                  hideFooter
                  disableSelectionOnClick
                  getRowId={(row) => row.id}
                  sx={{
                    border: 'none',
                    minWidth: 860,
                    '& .MuiDataGrid-columnHeader': { backgroundColor: (theme) => theme.palette.primary.main },
                    '& .MuiDataGrid-columnHeaderTitle': { color: 'white', fontWeight: 'bold' },
                    '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': { color: 'white' },
                    '& .MuiDataGrid-row:hover': { backgroundColor: '#FFF0F3' },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
          <CustomPagination
            page={accountPage}
            rowsPerPage={accountRowsPerPage}
            totalRows={accountRows.length}
            setPage={setAccountPage}
            setRowsPerPage={setAccountRowsPerPage}
            customRowsInput={accountCustomRowsInput}
            setCustomRowsInput={setAccountCustomRowsInput}
            customRowsActive={accountCustomRowsActive}
            setCustomRowsActive={setAccountCustomRowsActive}
          />
        </Grid>
      </Grid>

      <Dialog open={openPlatformForm} onClose={() => setOpenPlatformForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedPlatform ? 'Edit Platform' : 'Create Platform'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Platform Name" value={platformForm.name} onChange={(e) => setPlatformForm((prev) => ({ ...prev, name: e.target.value }))} />
          <TextField select fullWidth margin="normal" label="Active" value={platformForm.is_active ? 'true' : 'false'} onChange={(e) => setPlatformForm((prev) => ({ ...prev, is_active: e.target.value === 'true' }))}>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPlatformForm(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePlatform}>{selectedPlatform ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAccountForm} onClose={() => setOpenAccountForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedAccount ? 'Edit Account' : 'Create Account'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Platform"
            value={accountForm.platform}
            onChange={(e) => setAccountForm((prev) => ({ ...prev, platform: e.target.value }))}
          >
            {platformRows.map((platform) => (
              <MenuItem key={platform.id} value={platform.id}>{platform.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            margin="normal"
            label="Account"
            value={accountForm.purchasing_account}
            onChange={(e) => setAccountForm((prev) => ({ ...prev, purchasing_account: e.target.value }))}
          />
          <TextField
            select
            fullWidth
            margin="normal"
            label="Owner (Admin User)"
            value={accountForm.owner}
            onChange={(e) => setAccountForm((prev) => ({ ...prev, owner: e.target.value }))}
            disabled={!user?.is_superuser}
          >
            {owners.map((owner) => (
              <MenuItem key={owner.id} value={owner.id}>
                {owner.username || owner.email}
              </MenuItem>
            ))}
          </TextField>
          <TextField select fullWidth margin="normal" label="Active" value={accountForm.is_active ? 'true' : 'false'} onChange={(e) => setAccountForm((prev) => ({ ...prev, is_active: e.target.value === 'true' }))}>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAccountForm(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAccount}>{selectedAccount ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Delete {deleteTarget.type === 'platform' ? 'Platform' : 'Account'}</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this {deleteTarget.type || 'record'}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PurchasingReferences;
